import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { Button } from 'components/atoms/button'
import type { TicketSummary, TicketSummaryRow, TicketZoneSummaryRow } from 'graphql/generated/scheme'

interface CheckedInStatisticsModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	ticketSummary?: TicketSummary
	loading: boolean
	lastUpdated: Date
	onRefresh: () => void
}

const getCheckInRate = (checkedIn: number, total: number) => {
	if (total === 0) return 0
	return Math.round((checkedIn / total) * 100)
}

const formatLastUpdated = (date: Date) =>
	date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

export const CheckedInStatisticsModal: React.FC<CheckedInStatisticsModalProps> = ({
	open,
	setOpen,
	ticketSummary,
	loading,
	lastUpdated,
	onRefresh,
}) => {
	return (
		<ContentModal title="Checked In" open={open} setOpen={setOpen} size="lg">
			{/* Event name */}
			{ticketSummary?.event?.displayName && (
				<div className="mb-6 text-center">
					<h2 className="text-xl font-bold mb-1">{ticketSummary.event.displayName}</h2>
					<p className="text-sm text-secondary-text">Event Check-in Statistics</p>
				</div>
			)}

			{/* Last updated + refresh */}
			<div className="flex items-center justify-between mb-6">
				<span className="text-sm text-secondary-text">Last updated: {formatLastUpdated(lastUpdated)}</span>
				<Button onClick={onRefresh} disabled={loading} variant="secondary" className="flex items-center gap-2">
					Refresh
				</Button>
			</div>

			{loading ? (
				<div className="flex justify-center items-center py-12">
					<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="text-secondary-text" />
				</div>
			) : (
				<>
					{/* Summary stat cards */}
					<div className="grid grid-cols-3 gap-3 mb-6">
						{[
							{ label: 'Total Issued Tickets', value: ticketSummary?.totalIssuedTickets ?? 0 },
							{ label: 'Total Checked In', value: ticketSummary?.totalCheckedInTickets ?? 0 },
							{
								label: 'Check-in Rate',
								value: `${getCheckInRate(ticketSummary?.totalCheckedInTickets ?? 0, ticketSummary?.totalIssuedTickets ?? 0)}%`,
								highlight: true,
							},
						].map(card => (
							<div
								key={card.label}
								className="bg-primary-surface/[0.4] backdrop-blur-lg border border-translucent-plus-border rounded-xl p-4"
							>
								<div className="text-[11px] text-secondary-text uppercase tracking-wider font-semibold mb-1.5">
									{card.label}
								</div>
								<div
									className={
										card.highlight ? 'text-2xl font-bold text-success' : 'text-2xl font-bold'
									}
								>
									{card.value}
								</div>
							</div>
						))}
					</div>

					{/* Ticket & zone breakdown */}
					<div>
						<div className="text-[11px] text-secondary-text uppercase tracking-wider font-semibold mb-4">
							Ticket &amp; Zone Breakdown
						</div>
						<div className="space-y-3">
							{ticketSummary?.ticketSummaries && ticketSummary.ticketSummaries.length > 0 ? (
								ticketSummary.ticketSummaries.map((ticket: TicketSummaryRow, ticketIndex) => {
									const rate = getCheckInRate(ticket.totalCheckedInTickets, ticket.totalIssuedTickets)
									const scannedZones =
										ticket.zoneSummaries?.filter(z => z.totalCheckedInTickets > 0) ?? []
									return (
										<div
											key={ticket.eventTicket?.eventTicketId ?? ticketIndex}
											className="bg-primary-surface/[0.4] backdrop-blur-lg border border-translucent-plus-border rounded-xl p-4"
										>
											{/* Ticket header */}
											<div className="flex items-center justify-between gap-4 mb-3">
												<div className="flex items-center gap-2 min-w-0">
													<span className="font-semibold truncate">
														{ticket.eventTicket?.ticketType ?? 'Unknown Ticket'}
													</span>
													{ticket.eventTicket?.isPrivate && (
														<span className="flex-shrink-0 px-2 py-0.5 bg-accent-text/20 border border-accent-text/30 rounded-full text-[11px] text-accent-text">
															Private
														</span>
													)}
												</div>
												<div className="text-right flex-shrink-0">
													<div className="font-semibold">
														{ticket.totalCheckedInTickets}{' '}
														<span className="text-secondary-text font-normal">
															/ {ticket.totalIssuedTickets}
														</span>
													</div>
													<div className="text-xs text-secondary-text">
														{rate}% checked in
													</div>
												</div>
											</div>

											{/* Progress bar */}
											<div className="h-1 bg-white/[0.08] rounded-full mb-3 overflow-hidden">
												<div
													className="h-full rounded-full bg-success transition-all duration-500"
													style={{ width: `${Math.min(rate, 100)}%` }}
												/>
											</div>

											{/* Zone rows */}
											{scannedZones.length > 0 ? (
												<table className="w-full">
													<tbody>
														{scannedZones.map((zone: TicketZoneSummaryRow, zoneIndex) => (
															<tr
																key={zone.eventTicketZoneId ?? zoneIndex}
																className="border-t border-translucent-plus-border/50"
															>
																<td className="py-1.5 text-sm text-secondary-text">
																	{zone.zoneName ?? `Zone ${zoneIndex + 1}`}
																</td>
																<td className="py-1.5 text-sm text-right font-medium">
																	{zone.totalCheckedInTickets}
																</td>
															</tr>
														))}
													</tbody>
												</table>
											) : (
												<p className="text-xs text-secondary-text">
													No zone scan data available
												</p>
											)}
										</div>
									)
								})
							) : (
								<p className="text-center py-8 text-secondary-text">No ticket data available</p>
							)}
						</div>
					</div>
				</>
			)}
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface CheckedInStatisticsModalManagedProps {
	ticketSummary?: TicketSummary
	loading: boolean
	lastUpdated: Date
	onRefresh: () => void
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'checked-in-statistics': CheckedInStatisticsModalManagedProps
	}
}

export function ManagedCheckedInStatisticsModal({
	onClose,
	...rest
}: CheckedInStatisticsModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<CheckedInStatisticsModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
